import { Link } from "react-router-dom";
import { useState } from "react";
import { FileText, ChevronRight } from "lucide-react";
import { WorkCalendar } from "@/components/dashboard/WorkCalendar";
import { fmtDue, dueLabel, scoreLabel, KindTag, isGraded } from "@/components/dashboard/CourseWork";
import { HelpMenu } from "@/components/school/HelpMenu";
import { PROVIDER_LABEL } from "@/components/school/SchoolConnect";
import { JoinButton, ClassTime, useMinuteTick } from "@/components/school/SchoolClasses";
import { Video } from "lucide-react";

const groupKey = (i) => (i.source === "school" && i.course ? i.course : i.subject_id);
const slug = (x) => x.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Right = ({ i }) => {
  if (i.done) return <span className="text-xs text-stone-500 text-right" data-testid={`agenda-label-${i.id}`}>{scoreLabel(i) ? <b className="text-[#2E7D4F]">{isGraded(i) ? "Grade " : ""}{scoreLabel(i)}</b> : "Done"}</span>;
  if (i.points != null) return (
    <span className="text-right whitespace-nowrap" data-testid={`agenda-label-${i.id}`}>
      <span className="block font-display text-2xl text-[#1F3D33] leading-none">{i.points}<span className="text-[10px] ml-1 text-stone-500 font-sans">PTS</span></span>
      <span className="block text-[10px] uppercase tracking-wider text-stone-500 mt-1">Due: {i.due_at ? new Date(i.due_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) : fmtDue(i.date)}</span>
    </span>);
  return <span className="text-xs text-stone-500 text-right" data-testid={`agenda-label-${i.id}`}>{dueLabel(i) || "To do"}</span>;
};

const Row = ({ i, onToggle }) => (
  <li className="flex flex-wrap sm:flex-nowrap items-center gap-3 px-4 py-3" data-testid={`agenda-item-${i.id}`}>
    <input type="checkbox" checked={i.done} onChange={() => onToggle(i)} data-testid={`agenda-check-${i.id}`} aria-label={`Mark ${i.topic} done`} className="w-5 h-5 accent-[#2C4A3E] cursor-pointer" />
    <FileText className="w-4 h-4 shrink-0" style={{ color: i.color }} />
    <div className="flex-1 min-w-[60%] sm:min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-stone-500">{i.source === "school" ? `${i.course || i.subject_short} · ${i.manual ? "School" : PROVIDER_LABEL[i.provider || "canvas"]} assignment` : i.source === "teacher" ? `${i.subject_short} · Teacher assignment` : `${i.subject_short} · ${i.topic.startsWith("Review:") ? "Review" : "Lesson"}`} <KindTag i={i} where="ag" /></p>
      {i.source === "school" ? (i.link ? <a href={i.link} target="_blank" rel="noreferrer" className={`text-[#1F5C99] hover:underline ${i.done ? "line-through text-stone-400" : ""}`}>{i.topic}</a> : <span className={i.done ? "line-through text-stone-400" : "text-[#1F3D33]"}>{i.topic}</span>) :
        <Link to={`/learn/${i.subject_id}?topic=${encodeURIComponent(i.topic.replace(/^Review: /, ""))}`} className={`text-[#1F5C99] hover:underline ${i.done ? "line-through text-stone-400" : ""}`}>{i.topic}</Link>}
    </div>
    <HelpMenu i={i} where="ag" />
    <Right i={i} />
  </li>
);

const SubjectBlock = ({ gkey, items, onToggle }) => {
  const [showDone, setShowDone] = useState(false);
  const open = items.filter((i) => !i.done), done = items.filter((i) => i.done);
  const id = `${items[0].date}-${slug(gkey)}`;
  return (
    <div className="flex flex-col sm:flex-row rounded-2xl overflow-hidden border mb-3 bg-white" style={{ borderColor: `${items[0].color}66` }} data-testid={`agenda-block-${id}`}>
      <div className="sm:w-56 shrink-0 border-b sm:border-b-0 sm:border-r" style={{ borderColor: `${items[0].color}66` }}>
        <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#1F3D33]">{items[0].source === "school" && items[0].course ? items[0].course : items[0].subject_name}</p>
        <div className="h-2 sm:h-full sm:min-h-[24px]" style={{ background: items[0].color }} />
      </div>
      <div className="flex-1">
        <ul className="divide-y divide-[#EEE8DA]">{open.map((i) => <Row key={i.id} i={i} onToggle={onToggle} />)}</ul>
        {done.length > 0 && <button onClick={() => setShowDone(!showDone)} data-testid={`agenda-show-done-${id}`} className={`w-full text-left px-4 py-3 text-sm text-[#1F5C99] hover:bg-[#F7F3EB] inline-flex items-center gap-2 ${open.length ? "border-t border-[#EEE8DA]" : ""}`}>
          <ChevronRight className={`w-4 h-4 transition-transform ${showDone ? "rotate-90" : ""}`} />{showDone ? "Hide" : "Show"} {done.length} completed item{done.length === 1 ? "" : "s"}</button>}
        {showDone && <ul className="divide-y divide-[#EEE8DA] bg-[#FBF8F1]">{done.map((i) => <Row key={i.id} i={i} onToggle={onToggle} />)}</ul>}
      </div>
    </div>
  );
};

const Classes = ({ classes }) => {
  useMinuteTick();
  return !classes?.length ? null : (
  <ul className="mt-3 mb-3 space-y-2">{classes.map((c) => (
    <li key={c.uid} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#F6C9A3] bg-[#FFF8F1] px-4 py-2.5 text-sm" data-testid={`agenda-class-${c.uid}`}>
      <Video className="w-4 h-4 text-[#F28C38]" /><span className="font-medium text-[#2C4A3E] w-44 whitespace-nowrap"><ClassTime c={c} /></span>
      <span className="flex-1 text-[#1F3D33]">{c.title} <span className="text-xs text-stone-500">· {PROVIDER_LABEL[c.provider || "other"]} live class</span></span>
      <JoinButton c={c} small where="agenda" />
    </li>))}</ul>
  );
};

const Day = ({ title, items, onToggle, testId, classes }) => {
  const groups = items.reduce((m, i) => ({ ...m, [groupKey(i)]: [...(m[groupKey(i)] || []), i] }), {});
  return (
    <div className="mt-6" data-testid={testId}>
      <h3 className="font-display text-2xl text-[#2C4A3E]">{title}</h3>
      <Classes classes={classes} />
      <div className="mt-3">{Object.entries(groups).map(([g, its]) => <SubjectBlock key={g} gkey={g} items={its} onToggle={onToggle} />)}</div>
    </div>
  );
};

export const Agenda = ({ d, onToggle }) => {
  const [view, setView] = useState("agenda");
  const t = new Date(`${d.today}T12:00:00`);
  const tm = new Date(t); tm.setDate(t.getDate() + 1);
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  const sunday = new Date(t); sunday.setDate(t.getDate() + (7 - t.getDay()) % 7);
  const today = d.agenda.filter((i) => i.date === d.today);
  const tomorrow = d.agenda.filter((i) => i.date === iso(tm));
  const later = d.agenda.filter((i) => i.date > iso(tm) && i.date <= iso(sunday));
  const byDate = later.reduce((m, i) => ({ ...m, [i.date]: [...(m[i.date] || []), i] }), {});
  const cls = (day) => (d.school_classes || []).filter((c) => c.date === day);
  (d.school_classes || []).forEach((c) => { if (c.date > iso(tm) && c.date <= iso(sunday) && !byDate[c.date]) byDate[c.date] = []; });
  return (
    <section className="mt-12" data-testid="agenda">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[#2C4A3E] pb-3">
        <h2 className="font-display text-3xl text-[#2C4A3E]">My agenda</h2>
        <div className="flex p-1 rounded-full bg-[#F1EADB]" role="tablist">
          {["week", "month", "agenda"].map((v) => <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)} data-testid={`agenda-view-${v}`} className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${view === v ? "bg-[#2C4A3E] text-white" : "text-[#2C4A3E]"}`}>{v}</button>)}
        </div>
      </div>
      {view !== "agenda" ? <WorkCalendar view={view} today={d.today} onToggle={onToggle} classes={d.school_classes} /> : <>
      {today.length || cls(d.today).length ? <Day title={`Today · ${fmtDue(d.today)}`} items={today} onToggle={onToggle} testId="agenda-today" classes={cls(d.today)} /> : <p className="mt-6 text-stone-500" data-testid="agenda-today-empty">Nothing on today's list. Enjoy free exploring!</p>}
      {(tomorrow.length > 0 || cls(iso(tm)).length > 0) && <Day title={`Tomorrow · ${fmtDue(iso(tm))}`} items={tomorrow} onToggle={onToggle} testId="agenda-tomorrow" classes={cls(iso(tm))} />}
      {Object.keys(byDate).length > 0 && (
        <div className="mt-10" data-testid="agenda-upcoming">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C87D53] font-semibold">Upcoming this week</p>
          {Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, its]) => <Day key={date} title={fmtDue(date)} items={its} onToggle={onToggle} testId={`agenda-day-${date}`} classes={cls(date)} />)}
        </div>
      )}
      </>}
    </section>
  );
};
